import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, ClipboardList, FileText, MessageCircle, ArrowRight, X, Sparkles, CheckCircle2, Phone, Mail, Calendar, Home, HandHelping } from 'lucide-react';

const STORAGE_KEY = 'demo_tour_pending';
const ROLE_KEY = 'demo_tour_role';

const OWNER_STEPS = [
  {
    path: '/portal/dashboard',
    Icon: LayoutDashboard,
    title: 'Willkommen in der Tour!',
    body:
      'Hier sehen Sie Ihr persönliches Dashboard mit allen wichtigen Infos zu Ihrem Objekt, anstehenden Vorgängen und aktuellen Mitteilungen.',
  },
  {
    path: '/portal/vorgaenge',
    Icon: ClipboardList,
    title: 'Vorgänge & Anliegen',
    body:
      'Alle Anliegen, Schäden, Anfragen und Vorgänge an einem Ort – mit Status, Verlauf und Dokumenten. Mieter und Eigentümer können hier neue Anliegen melden.',
  },
  {
    path: '/portal/dokumente',
    Icon: FileText,
    title: 'Dokumente',
    body:
      'Verträge, Abrechnungen, Beschlüsse und alle weiteren Unterlagen finden Sie hier zentral – jederzeit abrufbar.',
  },
  {
    path: '/portal/ai-assistent',
    Icon: MessageCircle,
    title: 'Sonja – Ihr Service-Kontakt',
    body:
      'Eine Frage oder ein Schaden? Sonja vom Service-Team klärt Details, sieht sich Fotos an und sorgt dafür, dass Ihr Anliegen schnell beim richtigen Mitarbeiter ankommt.',
  },
];

const MIETER_STEPS = [
  {
    path: '/portal/mieter-portal',
    Icon: Home,
    title: 'Willkommen im Mieter-Portal!',
    body:
      'So erleben Ihre Mieter das Portal: Wohnung, Ansprechpartner, Mietdaten und aktuelle Mitteilungen der Verwaltung – übersichtlich auf einer Seite.',
  },
  {
    path: '/portal/vorgaenge',
    Icon: ClipboardList,
    title: 'Anliegen melden & verfolgen',
    body:
      'Tropfender Wasserhahn oder defekte Klingel? Mieter melden Schäden mit Foto direkt hier und sehen jederzeit Status und Verlauf – ohne Anruf oder Brief.',
  },
  {
    path: '/portal/dokumente',
    Icon: FileText,
    title: 'Dokumente',
    body:
      'Mietvertrag, Betriebskostenabrechnungen, Bescheinigungen und Hausordnung – zentral abgelegt und jederzeit abrufbar.',
  },
  {
    path: '/portal/self-service',
    Icon: HandHelping,
    title: 'Self-Service',
    body:
      'Mietbescheinigung anfordern, Kontaktdaten ändern, Zählerstände melden – viele Anliegen erledigen Mieter in wenigen Klicks selbst. Das entlastet Verwaltung und Eigentümer.',
  },
  {
    path: '/portal/ai-assistent',
    Icon: MessageCircle,
    title: 'Sonja – jederzeit erreichbar',
    body:
      'Sonja vom Service-Team beantwortet Fragen, nimmt Schadensmeldungen mit Fotos entgegen und leitet alles an den richtigen Mitarbeiter weiter – auch außerhalb der Bürozeiten.',
  },
];

const readRole = () => {
  try {
    const stored = localStorage.getItem(ROLE_KEY) || JSON.parse(localStorage.getItem('user') || '{}')?.role;
    return stored === 'mieter' ? 'mieter' : 'eigentuemer';
  } catch { return 'eigentuemer'; }
};

const trackTour = (role, event, step, totalSteps) => {
  const token = localStorage.getItem('token');
  if (!token) return;
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/musterzugang/tour-event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ role, event, step, total_steps: totalSteps }),
    keepalive: true,
  }).catch(() => {});
};

const DemoTourOverlay = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [showConversion, setShowConversion] = useState(false);
  const [role, setRole] = useState('eigentuemer');
  const STEPS = role === 'mieter' ? MIETER_STEPS : OWNER_STEPS;

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === '1') {
      const t = setTimeout(() => {
        const r = readRole();
        setRole(r);
        setActive(true);
        trackTour(r, 'started', 1, (r === 'mieter' ? MIETER_STEPS : OWNER_STEPS).length);
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  // Sync step to current path
  useEffect(() => {
    if (!active) return undefined;
    const idx = STEPS.findIndex(s => s.path === location.pathname);
    if (idx < 0) return undefined;
    const t = setTimeout(() => setStep(idx), 0);
    return () => clearTimeout(t);
  }, [location.pathname, active]);

  const close = () => {
    trackTour(role, 'skipped', step + 1, STEPS.length);
    setActive(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ROLE_KEY);
  };

  const closeAndConvert = () => {
    trackTour(role, 'completed', STEPS.length, STEPS.length);
    setActive(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ROLE_KEY);
    setShowConversion(true);
  };

  const goNext = () => {
    if (step < STEPS.length - 1) {
      const next = step + 1;
      setStep(next);
      navigate(STEPS[next].path);
    } else {
      // Tour beendet → Conversion-Modal zeigen
      closeAndConvert();
    }
  };

  if (!active) {
    // Conversion-Modal nach Tour-Ende
    if (showConversion) {
      return (
        <AnimatePresence>
          <motion.div
            key="conv-bg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            data-testid="demo-conversion-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', duration: 0.45 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            >
              <div className="bg-gradient-to-br from-brand-navy to-brand-navy-dark text-white p-6 text-center relative">
                <button
                  onClick={() => setShowConversion(false)}
                  className="absolute top-3 right-3 text-white/70 hover:text-white"
                  data-testid="demo-conversion-close"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="w-14 h-14 rounded-full bg-brand-gold/20 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-7 h-7 text-brand-gold" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Interessiert?</h2>
                <p className="text-white/85 text-sm leading-relaxed">
                  {role === 'mieter'
                    ? 'So einfach erleben Ihre Mieter die Verwaltung – weniger Anrufe, schnellere Abläufe, zufriedenere Bewohner. Sprechen Sie uns an, wir freuen uns auf Ihr Objekt.'
                    : 'Sie haben gerade einen ersten Eindruck bekommen. Sprechen Sie uns an — wir freuen uns auf Ihre Verwaltung.'}
                </p>
              </div>
              <div className="p-5 space-y-2.5">
                <a
                  href="tel:+4961342863266"
                  data-testid="demo-conversion-call"
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-brand-navy hover:bg-brand-navy/[0.03] transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-brand-navy/5 flex items-center justify-center group-hover:bg-brand-navy group-hover:text-brand-gold text-brand-navy transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-brand-navy">Direkt anrufen</p>
                    <p className="text-xs text-gray-500">06134 - 28 63 266 · Mo-Fr 9-14 Uhr</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-brand-navy group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href="mailto:info@hvkurz.de?subject=Anfrage%20nach%20Portal-Demo"
                  data-testid="demo-conversion-email"
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-brand-navy hover:bg-brand-navy/[0.03] transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg bg-brand-navy/5 flex items-center justify-center group-hover:bg-brand-navy group-hover:text-brand-gold text-brand-navy transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-brand-navy">E-Mail schreiben</p>
                    <p className="text-xs text-gray-500">info@hvkurz.de</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-brand-navy group-hover:translate-x-0.5 transition-transform" />
                </a>
                <button
                  type="button"
                  data-testid="demo-conversion-kontakt"
                  onClick={() => {
                    setShowConversion(false);
                    window.location.href = '/kontakt?source=demo-tour';
                  }}
                  className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-brand-navy hover:bg-brand-navy/[0.03] transition-all group text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-brand-gold/15 flex items-center justify-center group-hover:bg-brand-navy group-hover:text-brand-gold text-brand-navy transition-colors">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-brand-navy">Kontaktformular &amp; Beratungstermin</p>
                    <p className="text-xs text-gray-500">Wir melden uns kurzfristig</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-brand-navy group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button
                  onClick={() => setShowConversion(false)}
                  className="w-full pt-2 text-xs text-gray-400 hover:text-gray-600"
                >
                  Erstmal weiter im Demo-Portal schauen
                </button>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      );
    }
    return null;
  }
  const current = STEPS[step];
  const Icon = current.Icon;
  const isLast = step === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        key="demo-tour"
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ type: 'spring', duration: 0.4 }}
        className="fixed bottom-20 lg:bottom-4 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)]"
        data-testid="demo-tour-overlay"
      >
        <div className="bg-white rounded-xl shadow-2xl border border-brand-gold/40 overflow-hidden">
          <div className="bg-gradient-to-r from-brand-navy to-brand-navy-dark text-white px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-gold" />
              <span className="text-xs font-semibold tracking-wider uppercase">{role === 'mieter' ? 'Mieter-Tour' : 'Portal-Tour'}</span>
              <span className="text-[10px] text-white/60">{step + 1} / {STEPS.length}</span>
            </div>
            <button
              onClick={close}
              data-testid="demo-tour-close"
              className="text-white/70 hover:text-white"
              title="Tour beenden"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4">
            <div className="flex items-start gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-brand-gold/15 text-brand-navy flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-brand-navy leading-tight">{current.title}</h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">{current.body}</p>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5 mb-3">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i < step ? 'w-4 bg-brand-navy' : i === step ? 'w-6 bg-brand-gold' : 'w-4 bg-gray-200'
                  }`}
                />
              ))}
            </div>

            <div className="flex items-center justify-between gap-2">
              <button
                onClick={close}
                data-testid="demo-tour-skip"
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Tour überspringen
              </button>
              <button
                onClick={goNext}
                data-testid="demo-tour-next"
                className="px-4 py-2 bg-brand-navy text-white text-xs font-semibold rounded-lg hover:bg-brand-navy-dark transition-colors flex items-center gap-1.5"
              >
                {isLast ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Tour beenden
                  </>
                ) : (
                  <>
                    Weiter <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DemoTourOverlay;
